import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { ReactElement, ReactNode } from 'react';

type Props = {
  title: string | ReactElement;
  className?: string;
  children?: ReactElement | ReactElement[];
  description?: string;
  imageSrc?: string;
  onClick: (event: unknown) => void;
  TitleDecorator?: ReactNode;
  DescriptionDecorator?: ReactNode;
  TopRightDecorator?: ReactNode;
  minimize?: boolean;
  disabled?: boolean;
};

export default function StepButton({
  onClick,
  title,
  children,
  className,
  description,
  imageSrc,
  TitleDecorator,
  DescriptionDecorator,
  TopRightDecorator,
  minimize = false,
  disabled = false,
}: Props) {
  return (
    <button
      className={classNames(
        'relative flex w-full flex-col justify-between overflow-hidden rounded-xl px-6 text-left text-black',
        className,
        minimize ? 'py-2' : 'py-4',
        disabled ? 'bg-gray-dark' : 'hover:bg-accent bg-white',
      )}
      data-testid="step-button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {TopRightDecorator ? (
        <div className="absolute right-6 top-2 text-xs">
          {TopRightDecorator}
        </div>
      ) : null}

      <div
        className={classNames(
          'flex w-full flex-row justify-between gap-3',
          !!TopRightDecorator && 'max-sm:py-3',
        )}
      >
        {imageSrc ? (
          <div
            className={`relative ${
              minimize ? 'h-15 w-15' : 'h-20 w-20'
            } shrink-0 rounded-full`}
          >
            <img
              src={imageSrc}
              alt=""
              className="h-full w-full rounded-full object-cover"
            />
            {disabled && (
              <div className="bg-gray-dark/50 absolute inset-0 rounded-full" />
            )}
          </div>
        ) : null}

        {/*Title and description*/}
        <div className="flex w-full flex-col md:text-base">
          {typeof title === 'string' ? (
            <p className="text-sm font-normal leading-[28px] md:leading-[32px]">
              {title}
            </p>
          ) : (
            title
          )}

          {description ? (
            <div className="flex items-center text-xs leading-[24px] text-black/50">
              <span>{description}</span>
            </div>
          ) : null}

          {DescriptionDecorator ? (
            <div className="flex items-center justify-between whitespace-nowrap text-xs leading-[24px]">
              {DescriptionDecorator}
            </div>
          ) : null}
        </div>

        {TitleDecorator ? (
          <div className="flex items-center justify-between whitespace-nowrap text-xs leading-[24px]">
            {TitleDecorator}
          </div>
        ) : null}
      </div>

      {children}
    </button>
  );
}

type AnimatedProps = {
  children: ReactElement | ReactElement[];
  order: number;
  className?: string;
};

const getAnimationStyle = (i: number) => ({
  hidden: { opacity: 0, translateY: '5%' },
  show: {
    opacity: 1,
    translateY: '0%',
    transition: {
      delay: i * 0.07,
      duration: 0.3,
    },
  },
});

export function AnimatedStep({ children, order, className }: AnimatedProps) {
  return (
    <motion.div
      variants={getAnimationStyle(order)}
      initial="hidden"
      animate="show"
      layout
      className={classNames('w-full', className)}
    >
      {children}
    </motion.div>
  );
}
